// @ts-check
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Errors from 'shared/components/Errors';
import {
  TextInput,
  TextField,
  DateMaskField,
  SelectField,
  PlainCountySelectionField,
} from 'shared/containers/portal_layout/HForm';
import { FieldArray } from 'utils/reduxFormWrapper';
import SpinnerButton from 'shared/components/SpinnerButton';
import SearchingTextInput from 'agent_portal/components/SearchingTextInput';
import PatientCoverageForm from 'shared/components/PatientCoverage/Form';
import { required } from 'utils/validation';
import FileAttachments from 'shared/components/Referrals/FileAttachments';
import queryString from 'query-string';
import { formValueSelector } from 'utils/reduxFormWrapper';
import { connect } from 'react-redux';
import ReferringProvidersActions from 'agent_portal/actions/ReferringProvidersActions';
import ReferringLocationsActions from 'agent_portal/actions/ReferringLocationsActions';
import { change } from 'redux-form';
import InboundOrOutboundChooser from './InboundOrOutboundChooser';
import InternalReferringLocationAndProvider from './InternalReferringLocationAndProvider';
import ExternalReferringLocationAndProvider from './ExternalReferringLocationAndProvider';
import NetworkLogo from 'shared/components/NetworkLogo';
import { useReferralSettings } from './useReferralSettings';
import LoadingContent from 'shared/components/LoadingContent';
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-google-places-autocomplete';
import { structureAddressParts } from 'preferral-common/googleMaps/structureAddressParts';
import { useQuery } from 'react-query';
import api from 'utils/api';

const FullAddressFields = (props) => {
  const { states, setField } = props;

  const [address, setAddress] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [poBox, setPoBox] = useState(false);
  const [geocodedAddress, setGeocodedAddress] = useState(null);

  const placeId = address && address.value && address.value.place_id;

  useEffect(() => {
    if (placeId) {
      setIsGeocoding(true);
      geocodeByPlaceId(placeId).then((result) => {
        setIsGeocoding(false);
        const address = structureAddressParts(result[0]);
        setField('streetAddress', address.streetAddress);
        setField('city', address.city);
        setField('stateAbbreviation', address.stateAbbreviation);
        setField('zip', address.zip);
        setField('latitude', address.latitude);
        setField('longitude', address.longitude);
        setGeocodedAddress(address);
      });
    }
  }, [placeId, setIsGeocoding]);

  return (
    <>
      {!geocodedAddress && !poBox ? (
        <>
          <div className="clearfix">
            <div className="control-group new-booking-form-group">
              <label className="control-label">Patient Address</label>
              <div className="controls w-full max-w-lg">
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_PLACES_API_KEY}
                  apiOptions={{ language: 'en', region: 'us' }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['us'],
                    },
                    types: ['address'],
                  }}
                  selectProps={{
                    value: address,
                    onChange: setAddress,
                    isDisabled: isGeocoding,
                    isLoading: isGeocoding,
                    placeholder: 'Patient Address',
                    noOptionsMessage: () => 'Type to Search',
                  }}
                />
              </div>
            </div>
          </div>
          <div className="-mt-1.5 flex items-center mb-2.5">
            <input
              id="isPoBox"
              type="checkbox"
              checked={poBox}
              onChange={() => setPoBox(!poBox)}
              className="form-checkbox w-4 h-4 text-indigo-600 transition duration-150 ease-in-out"
            />
            <label
              htmlFor="isPoBox"
              className="block ml-2 text-xs leading-5 text-gray-900"
            >
              Patient address is PO Box
            </label>
          </div>
        </>
      ) : null}

      <div className={!geocodedAddress && !poBox ? 'is-hidden' : ''}>
        <TextField
          id="streetAddress"
          label="Street Address"
          className="input w-full max-w-md"
          name="streetAddress"
          placeholder="Patient Address"
          validate={required}
        />

        <div className="-mt-1.5 flex items-center mb-2.5">
          <input
            id="isPoBox"
            type="checkbox"
            checked={poBox}
            onChange={() => setPoBox(!poBox)}
            className="form-checkbox w-4 h-4 text-indigo-600 transition duration-150 ease-in-out"
          />
          <label
            htmlFor="isPoBox"
            className="block ml-2 text-xs leading-5 text-gray-900"
          >
            Patient address is PO Box
          </label>
        </div>

        <div className="clearfix">
          <div className="control-group new-booking-form-group">
            <div className="controls">
              <TextInput
                id="streetAddressLine2"
                className="input w-full max-w-md"
                name="streetAddressLine2"
                placeholder="Street Address Line 2"
              />
            </div>
          </div>
        </div>

        <TextField
          id="city"
          label="City"
          className="input w-full max-w-md"
          name="city"
          placeholder="City"
          validate={required}
        />

        <SelectField
          id="stateAbbreviation"
          label="State"
          wrapperProps={{
            style: { clear: 'both' },
          }}
          className="w-full max-w-sm"
          name="stateAbbreviation"
          placeholder="State"
          options={states}
          valueKey="abbreviation"
          labelKey="name"
          validate={required}
        />

        <TextField
          id="zip"
          className="input w-full max-w-sm"
          label="Zip"
          name="zip"
          placeholder="Zip"
          validate={[required, validateZipLength]}
        />

        <div className="is-hidden">
          <TextInput id="latitude" name="latitude" />
          <TextInput id="latitude" name="longitude" />
        </div>
      </div>
    </>
  );
};

const PresentationComponent = (props) => {
  const {
    referringProviders,
    referringLocations,
    inboundReferrableSpecialties,
    referrableSpecialties,
    selectReferringProvider,
    selectReferringLocation,
    removeExistingPatient,
    createReferralProfile,
    handleSubmit,
    submitting,
    error,
    form,
    zip,
    selectedSpecialtyId,
    shouldUseExistingPatient,
    existingPatient,
    bringLastNameForward,
    pushLastNameBackwards,
    shouldStackLastNameOnTop,
    enteredBySender,
    currentOrganizationId,
    network,
    dispatch,
  } = props;

  const { isFetched, data } = useReferralSettings(network && network.id);

  const statesQuery = useQuery('states', () =>
    api.get('/shared/api/states').then((resp) => resp.data)
  );

  const states = statesQuery.data || [];

  // console.log({ referralSettings });

  useEffect(() => {
    ReferringProvidersActions.fetchReferringProviders(props.dispatch).then(
      props.autoSelectReferringProvider
    );
  }, []);

  // const specialtyOptions = enteredBySender
  //   ? referrableSpecialties
  //   : inboundReferrableSpecialties;
  const specialtyOptions =
    !enteredBySender && !!inboundReferrableSpecialties
      ? inboundReferrableSpecialties
      : referrableSpecialties;

  const referralFullAddressInputEnabled = !!(
    (data && (
      data.referralSettings.referralFullAddressInputEnabled || (!enteredBySender && data.referralSettings.inboundReferralFullAddressInputEnabled)
      )))

  function setField(fieldName, fieldValue) {
    return dispatch(change(form, fieldName, fieldValue));
  }

  const showInboundToggle =
    network.id === 'preferral' || network.id === currentOrganizationId;

  return (
    <div>
      {!isFetched ? (
        <LoadingContent />
      ) : (
        <>
          <div style={{ margin: '12px auto', width: '80%', maxWidth: 300 }}>
            <NetworkLogo network={network} />
          </div>

          <div className="referral-form-instruction">
            Please fill in all fields on this form.
            {shouldUseExistingPatient ? (
              <span
                className="referral-form-clear-form-link"
                onClick={removeExistingPatient}
              >
                Clear Existing Patient
              </span>
            ) : null}
          </div>

          <form
            className="form-horizontal"
            onSubmit={handleSubmit(createReferralProfile)}
          >
            <Errors errors={error || []} />
            {/* TODO: separate out this inbound/outbound stuff to a different place. Maybe in the referral inbox */}
            <div className={showInboundToggle ? '' : 'is-hidden'}>
              <InboundOrOutboundChooser />
            </div>
            {enteredBySender ? (
              <InternalReferringLocationAndProvider
                referringProviders={referringProviders}
                referringLocations={referringLocations}
                selectReferringProvider={selectReferringProvider}
                selectReferringLocation={selectReferringLocation}
              />
            ) : (
              <ExternalReferringLocationAndProvider form={form} />
            )}
            {shouldUseExistingPatient ? (
              <div>
                <div className="control-group new-booking-form-group">
                  <label className="control-label">Patient Last Name</label>
                  <div className="controls">{existingPatient.lastName}</div>
                </div>

                <div className="control-group new-booking-form-group">
                  <label className="control-label">Patient First Name</label>
                  <div className="controls">{existingPatient.firstName}</div>
                </div>

                <div className="control-group new-booking-form-group">
                  <label className="control-label">Patient DOB:</label>
                  <div className="controls">{existingPatient.dobString}</div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  position: 'relative',
                  zIndex: shouldStackLastNameOnTop ? 2 : 0,
                }}
              >
                <div className="control-group new-booking-form-group">
                  <label className="control-label">Patient Last Name</label>
                  <div className="controls">
                    <SearchingTextInput
                      name="lastName"
                      onFocus={bringLastNameForward}
                      onBlur={pushLastNameBackwards}
                      pathForResult={(result) => {
                        const paramString = queryString.stringify({
                          selected_network_id: network.id,
                          specialty_id: selectedSpecialtyId,
                          existing_patient_id: result.id,
                        });
                        return `/a/portal/referral_profiles/new?${paramString}`;
                      }}
                      validate={required}
                    />
                  </div>
                </div>

                <TextField
                  name="firstName"
                  label="Patient First Name"
                  validate={required}
                />
                <DateMaskField
                  name="dobString"
                  label="Patient DOB"
                  mask="dob"
                  placeholder="mm/dd/yyyy"
                  validate={required}
                />
              </div>
            )}

            <PatientCoverageForm
              form={form}
              prefix="patientCoverageAttributes"
            />

            {referralFullAddressInputEnabled ? (
              <FullAddressFields states={states} setField={setField} />
            ) : (
              <>
                <TextField
                  name="zip"
                  label="Patient Zip"
                  className="input-small"
                  placeholder="zip code"
                  validate={required}
                />

                <PlainCountySelectionField
                  className="input-xlarge"
                  name="fipsCode"
                  label="Patient County"
                  zip={zip}
                  validate={required}
                />
              </>
            )}

            <div
              className={referrableSpecialties.length === 1 ? 'is-hidden' : ''}
            >
              <SelectField
                name="specialtyId"
                label="Select Specialty"
                options={specialtyOptions}
                wrapperProps={{
                  style: { clear: 'both' },
                }}
                className="input-xlarge specialty-select"
                valueKey="id"
                labelKey="name"
                validate={required}
                autoSelect={referrableSpecialties.length === 1}
              />
            </div>

            <div className="control-group new-booking-form-group">
              <label className="control-label">Attach File(s)</label>
              <div className="controls">
                <span style={{ color: '#aaa', fontSize: '10px' }}>
                  You can always add files later
                </span>
                <FieldArray
                  name="fileAttachmentsAttributes"
                  component={FileAttachments}
                />
              </div>
            </div>

            <div className="form-actions">
              <SpinnerButton
                type="submit"
                className="btn btn-primary"
                loading={submitting}
              >
                Continue
              </SpinnerButton>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

function mapStateToProps(state, ownProps) {
  const formSelector = formValueSelector(ownProps.form);
  const inboundReferrableSpecialties = state.referralWizard.inboundReferrableSpecialties;

  return {
    inboundReferrableSpecialties,
    referringProviders: state.referringProviders,
    referringLocations: state.referringLocations,
    selectedSpecialtyId: formSelector(state, 'specialtyId'),
    zip: formSelector(state, 'zip'),
    enteredBySender: formSelector(state, 'enteredBySender'),
    currentOrganizationId: state.currentAgent.currentEmployment.organizationId,
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  const selectReferringLocation = (referringLocationId) => {
    dispatch(
      change(ownProps.form, 'internalReferringLocationId', referringLocationId)
    );
  };

  const selectReferringProvider = (referringProviderEmployment) => {
    if (referringProviderEmployment && referringProviderEmployment.id) {
      dispatch(
        change(
          ownProps.form,
          'internalReferringProviderEmployment',
          referringProviderEmployment
        )
      );
      ReferringLocationsActions.fetchReferringLocationsForProvider(
        dispatch,
        referringProviderEmployment.id
      ).then(autoSelectReferringLocation);
    }
  };

  const autoSelectReferringLocation = () => {
    // uses redux-thunk
    return dispatch((_, getState) => {
      const referringLocations = getState().referringLocations.items;
      if (referringLocations.length > 0) {
        selectReferringLocation(referringLocations[0].id);
      }
    });
  };

  const autoSelectReferringProvider = () => {
    // uses redux-thunk
    return dispatch((_, getState) => {
      const referringProviders = getState().referringProviders.items;
      if (referringProviders.length > 0) {
        selectReferringProvider(referringProviders[0]);
      }
    });
  };

  return {
    dispatch,
    selectReferringProvider,
    selectReferringLocation,
    autoSelectReferringProvider,
  };
}

const ConnectedPresentationComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(PresentationComponent);

// These are the props that come in as ownProps during connection
ConnectedPresentationComponent.propTypes = {
  createReferralProfile: PropTypes.func.isRequired,
  removeExistingPatient: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired, // This comes from redux-form
  submitting: PropTypes.bool.isRequired, // redux-form
  error: PropTypes.string, // redux-form
  form: PropTypes.string.isRequired, // The name of the form, so we can pass it to PatientCoverage component so it uses the same form in redux state
  shouldUseExistingPatient: PropTypes.bool,
  existingPatient: PropTypes.object,
  bringLastNameForward: PropTypes.func.isRequired,
  pushLastNameBackwards: PropTypes.func.isRequired,
  shouldStackLastNameOnTop: PropTypes.bool.isRequired,
};

// These are the additional props provided by `connect`
PresentationComponent.propTypes = {
  zip: PropTypes.string, // We need this so we can pass it to the PlainCountySelectionField component
  selectedSpecialtyId: PropTypes.string, // We need this so we can pass it in the URL when we clear/select existing patient
  enteredBySender: PropTypes.bool.isRequired,
  referringProviders: PropTypes.object.isRequired,
  referringLocations: PropTypes.object.isRequired,
  selectReferringProvider: PropTypes.func.isRequired,
  selectReferringLocation: PropTypes.func.isRequired,
};

export default ConnectedPresentationComponent;

function validateZipLength(value) {
  if (value && value.length !== 5) {
    return 'Must be 5 digits';
  }
  return null;
}
